// Define roles enum to match backend exactly
export const UserRole = {
  ADMIN: 'ADMIN',
  GUEST_APP_MANAGER: 'GUEST_APP_MANAGER',
  MANAGER: 'MANAGER',
  MAINTENANCE: 'MAINTENANCE',
  HOUSEKEEPER: 'HOUSEKEEPER',
  HOUSEKEEPER_MANAGER: 'HOUSEKEEPER_MANAGER',
  INSPECTOR: 'INSPECTOR',
  PROPERTY_MANAGER: 'PROPERTY_MANAGER',
  CALL_CENTER: 'CALL_CENTER',
  USER: 'USER'
};

// Add LocationPreference enum
export const LocationPreference = {
  KIAWAH: 'KIAWAH',
  ISLE_OF_PALMS: 'ISLE_OF_PALMS',
  ISLE_OF_PALMS_WILD_DUNES: 'ISLE_OF_PALMS_WILD_DUNES',
  SEABROOK_ISLAND: 'SEABROOK_ISLAND'
};

// Add available areas array with grouped options
export const AVAILABLE_AREAS = [
  { value: LocationPreference.KIAWAH, label: 'Kiawah' },
  { value: LocationPreference.ISLE_OF_PALMS, label: 'Isle of Palms' },
  { value: LocationPreference.ISLE_OF_PALMS_WILD_DUNES, label: 'Isle of Palms - Wild Dunes' },
  { value: LocationPreference.SEABROOK_ISLAND, label: 'Seabrook Island' }
];

// Add grouped area options for easier selection
export const AREA_GROUPS = [
  { 
    value: 'ISLE_OF_PALMS_GROUP', 
    label: 'Isle of Palms + Wild Dunes',
    areas: [LocationPreference.ISLE_OF_PALMS, LocationPreference.ISLE_OF_PALMS_WILD_DUNES]
  },
  { 
    value: 'KIAWAH_SEABROOK_GROUP', 
    label: 'Kiawah + Seabrook',
    areas: [LocationPreference.KIAWAH, LocationPreference.SEABROOK_ISLAND]
  }
];

// Updated to use the enum values directly
export const AVAILABLE_ROLES = [
  { value: UserRole.ADMIN, label: 'Admin' },
  { value: UserRole.GUEST_APP_MANAGER, label: 'Guest App Manager' },
  { value: UserRole.MAINTENANCE, label: 'Maintenance' },
  { value: UserRole.HOUSEKEEPER, label: 'Housekeeper' },
  { value: UserRole.HOUSEKEEPER_MANAGER, label: 'Housekeeper Manager' },
  { value: UserRole.INSPECTOR, label: 'Inspector' },
  { value: UserRole.PROPERTY_MANAGER, label: 'Property Manager' },
  { value: UserRole.CALL_CENTER, label: 'Call Center' }
];

// Helper function to conditionally log only in development
export const devLog = (...args) => {
  if (process.env.NODE_ENV !== 'production') {
    console.log(...args);
  }
};

// Helper function to conditionally log errors only in development
export const devError = (...args) => {
  if (process.env.NODE_ENV !== 'production') {
    console.error(...args);
  }
};

// Helper function to handle group selections
export const handleAreaGroupToggle = (groupValue, currentAreas, setAreas) => {
  const group = AREA_GROUPS.find(g => g.value === groupValue);
  if (!group) return;

  const hasAllGroupAreas = group.areas.every(area => currentAreas.includes(area));
  
  if (hasAllGroupAreas) {
    // Remove all group areas
    setAreas(currentAreas.filter(area => !group.areas.includes(area)));
  } else {
    // Add all group areas (avoiding duplicates)
    const newAreas = [...new Set([...currentAreas, ...group.areas])];
    setAreas(newAreas);
  }
};

// Form validation function
export const validateForm = (formData) => {
  if (!formData.firstName.trim()) {
    throw new Error('First name is required');
  }
  if (!formData.email.trim()) {
    throw new Error('Email is required');
  }
  if (!formData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
    throw new Error('Please enter a valid email address');
  }
  if (formData.password && formData.password.length < 6) {
    throw new Error('Password must be at least 6 characters long');
  }
  if (!formData.roles || !formData.roles.length) {
    throw new Error('Role is required');
  }
  if (!Object.values(UserRole).includes(formData.roles[0])) {
    throw new Error('Invalid role selected');
  }
  // Phone number validation - if provided, must be valid format
  if (formData.phoneNumber && formData.phoneNumber.trim() && 
      !formData.phoneNumber.match(/^(\+?1?[ -]?)?\(?([0-9]{3})\)?[ -]?([0-9]{3})[ -]?([0-9]{4})$/)) {
    throw new Error('Please provide a valid phone number format (e.g., 843-560-3110, (843) 560-3110, 8435603110)');
  }
  // Salary validation - if provided, must be valid number
  if (formData.salary && formData.salary.trim()) {
    const salaryNum = parseFloat(formData.salary);
    if (isNaN(salaryNum) || salaryNum < 0 || salaryNum > 999999.99) {
      throw new Error('Please provide a valid salary amount (0-999999.99)');
    }
  }
};

// Default form data
export const getDefaultFormData = () => ({
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  password: '',
  roles: [UserRole.HOUSEKEEPER],
  locationPreference: '',
  areas: [],
  salary: ''
});

// Default invite data
export const getDefaultInviteData = () => ({
  email: '',
  roles: [UserRole.HOUSEKEEPER]
});

// Salary formatting utilities
export const formatSalary = (value) => {
  // Remove all non-numeric characters except decimal point
  const cleaned = value.replace(/[^\d.]/g, '');
  
  // Ensure only one decimal point
  const parts = cleaned.split('.');
  if (parts.length > 2) {
    return parts[0] + '.' + parts.slice(1).join('');
  }
  
  // Limit to 2 decimal places
  if (parts[1] && parts[1].length > 2) {
    return parts[0] + '.' + parts[1].substring(0, 2);
  }
  
  return cleaned;
};

export const validateSalary = (salary) => {
  if (!salary) return true; // Allow empty salary
  const numValue = parseFloat(salary);
  return !isNaN(numValue) && numValue >= 0 && numValue <= 999999.99;
};
